import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import {
  FINAL_STATUSES,
  UploadedReceipt,
  UploadedReceiptStatus,
} from '../../../types';
import { getUploadedReceipts } from '../apis/uploadedReceiptApi';

// ── Toggle this to `true` to use mock data for UI testing ──
const USE_MOCK_DATA = false;

const makeMockReceipt = (
  index: number,
  status: UploadedReceiptStatus,
  overrides?: Partial<UploadedReceipt>
): UploadedReceipt => ({
  id: `mock-receipt-${index}`,
  userId: 'mock-user',
  storageKey: `receipts/mock-${index}.pdf`,
  contentType: 'application/pdf',
  originalFileName: `Invoice-${String(index).padStart(3, '0')}.pdf`,
  fileSizeInBytes: 102400 + index * 5000,
  status,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  ...overrides,
});

const MOCK_LEXOFFICE_BOOKING = {
  voucherDate: '2026-03-01',
  dueDate: '2026-03-15',
  totalGrossAmount: 1190.0,
  totalTaxAmount: 190.0,
  taxType: 'gross' as const,
  currency: 'EUR',
  voucherNumber: 'INV-2026-001',
  remark: 'Office supplies Q1 2026',
  type: 'purchaseinvoice' as const,
  contactName: 'Bürobedarf GmbH',
  useCollectiveContact: false,
  voucherItems: [
    {
      amount: 1190.0,
      taxAmount: 190.0,
      taxRatePercent: 19,
      categoryId: '8f8664a1-fd86-11e1-a21f-0800200c9a66',
    },
  ],
};

const MOCK_RECEIPTS: UploadedReceipt[] = [
  // Section 1: Processing
  makeMockReceipt(1, 'pending'),
  makeMockReceipt(2, 'text-extracting'),

  // Section 2: Ready for Generation
  makeMockReceipt(3, 'ready', {
    textContent: 'Extracted text from invoice...',
  }),
  makeMockReceipt(4, 'text-extraction-failed', {
    errorMessage:
      'Could not extract text from scanned PDF. The image quality is too low.',
  }),
  makeMockReceipt(5, 'generation-scheduled'),
  makeMockReceipt(6, 'generating'),
  makeMockReceipt(7, 'generation-failed', {
    errorMessage:
      'Failed to generate booking data: LLM context limit exceeded.',
  }),

  // Section 3: Booking Review
  makeMockReceipt(8, 'generated', { lexOfficeBooking: MOCK_LEXOFFICE_BOOKING }),
  makeMockReceipt(9, 'modified', {
    lexOfficeBooking: {
      ...MOCK_LEXOFFICE_BOOKING,
      contactName: 'Updated Contact GmbH',
      remark: 'Modified remark',
    },
  }),
  makeMockReceipt(10, 'import-scheduled', {
    lexOfficeBooking: MOCK_LEXOFFICE_BOOKING,
  }),
  makeMockReceipt(11, 'importing', {
    lexOfficeBooking: MOCK_LEXOFFICE_BOOKING,
  }),
  makeMockReceipt(12, 'imported', { lexOfficeBooking: MOCK_LEXOFFICE_BOOKING }),
  makeMockReceipt(13, 'import-failed', {
    lexOfficeBooking: MOCK_LEXOFFICE_BOOKING,
    errorMessage: 'Lex Office API error: Contact "Bürobedarf GmbH" not found.',
  }),
];

/**
 * Fetches uploaded receipts by IDs with polling.
 * Polls every 10s while any receipt is in a non-final state.
 * Final states: generated, modified, imported, text-extraction-failed, generation-failed, import-failed
 */
export const useUploadedReceiptsQuery = (receiptIds: string[]) => {
  const { getAccessTokenSilently } = useAuth0();

  const {
    data: receipts = [],
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ['uploaded-receipts', receiptIds],
    queryFn: async () => {
      if (USE_MOCK_DATA) return MOCK_RECEIPTS;
      if (receiptIds.length === 0) return [];
      const token = await getAccessTokenSilently();
      return getUploadedReceipts(receiptIds, token);
    },
    enabled: USE_MOCK_DATA || receiptIds.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 10 * 1000, // 10 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: query => {
      if (USE_MOCK_DATA) return false;
      const data = query.state.data;
      if (data && data.length > 0) {
        const allFinal = data.every(receipt =>
          FINAL_STATUSES.includes(receipt.status)
        );
        return allFinal ? false : 10 * 1000;
      }
      return 10 * 1000;
    },
    refetchIntervalInBackground: false,
  });

  const error = queryError?.message || null;

  return {
    receipts,
    loading,
    error,
  };
};
